import { formatPlaceTitle } from '@/utils/formatPlaceTitle';
import { isScenicCruisingName } from '@/utils/itinerary';
import {
  formatFromPrice,
  isRealTourUrl,
  isRealViatorUrl,
  isSeaDayName,
  portIdentityKey,
  portsMatch,
} from '@/utils/shoreExcursions';

/**
 * CP-160 + CP-214: one port-by-port timeline on Detail.
 *
 * Pure model. Input is the scoped itinerary days (buildItinerarySection), the
 * single /cruises/:id/excursions payload, and the cruise duration. Output is
 * what the timeline renders: stops, the header summary, and whether any Viator
 * link is on screen (that decides the one disclosure).
 *
 * Honesty rules carried over from CP-159 / CP-130 / CP-165 / CP-223:
 *  - dates: matching stamped rows, or derived departure+(N-1) when undated;
 *    template itineraries and sailings with no departure stay "Day N" only
 *  - private islands and sea days never list tours
 *  - embark / debark / private-island notes are the backend's exact wording
 *  - tours are real Viator products only, in the backend's order
 */

export const TOURS_PER_STOP = 3;
export const SEE_ALL_LABEL = 'See all tours on Viator';

/** Port count and sea-day count come from here for the header and the sidebar. */

function nameOf(day) {
  if (!day || typeof day !== 'object') return '';
  const value = day.port || day.location || '';
  return typeof value === 'string' ? value.trim() : '';
}

/** A day with no port name, a "sea day" name, or a scenic stretch is not a port call. */
export function isNonPortDay(day) {
  const name = nameOf(day);
  if (!name) return true;
  return isSeaDayName(name) || isScenicCruisingName(name);
}

/**
 * Distinct ports on the schedule. Embark and debark at the same port count
 * once. Sea days (and scenic cruising) are not ports.
 */
export function countDistinctPorts(days) {
  const seen = new Set();
  (Array.isArray(days) ? days : []).forEach((day) => {
    if (isNonPortDay(day)) return;
    const key = portIdentityKey(nameOf(day));
    if (key) seen.add(key);
  });
  return seen.size;
}

export function countSeaDays(days) {
  return (Array.isArray(days) ? days : []).filter((day) => isNonPortDay(day)).length;
}

/**
 * One helper for the header line and the sidebar. With a day-by-day schedule
 * it counts from the schedule; otherwise from the stored port names (no sea
 * days can be known from a bare name list).
 */
export function summarizeItinerary(days, storedPorts) {
  const list = Array.isArray(days) ? days : [];
  if (list.length) {
    return { ports: countDistinctPorts(list), seaDays: countSeaDays(list), fromSchedule: true };
  }
  const names = (Array.isArray(storedPorts) ? storedPorts : [])
    .filter((name) => typeof name === 'string' && name.trim())
    .map((name) => ({ port: name.trim() }));
  return { ports: countDistinctPorts(names), seaDays: 0, fromSchedule: false };
}

function plural(n, one, many) {
  return n === 1 ? `${n} ${one}` : `${n} ${many}`;
}

export function portsLabel(count) {
  return plural(count, 'port', 'ports');
}

export function seaDaysLabel(count) {
  return plural(count, 'sea day', 'sea days');
}

/** "4 nights · 3 ports · 1 sea day". Unknown or zero parts are left out. */
export function formatSummaryLine({ nights, ports, seaDays }) {
  const parts = [];
  const n = Math.round(Number(nights));
  if (Number.isFinite(n) && n > 0) parts.push(plural(n, 'night', 'nights'));
  if (ports > 0) parts.push(portsLabel(ports));
  if (seaDays > 0) parts.push(seaDaysLabel(seaDays));
  return parts.join(' \u00b7 ');
}

/**
 * "Wed, Nov 11" from a civil YYYY-MM-DD. Uses UTC Y-M-D parts + timeZone UTC so
 * a bare ISO date is never parsed as UTC midnight in America/New_York.
 */
export function formatTimelineDate(ymd) {
  const match = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

/**
 * Port names keep the API casing ("Perfect Day CocoCay"). Only an all-lower or
 * all-upper name is title-cased.
 */
export function displayPortName(name) {
  const text = typeof name === 'string' ? name.trim() : '';
  if (!text) return '';
  const mixed = /[a-z]/.test(text) && /[A-Z]/.test(text);
  return mixed ? text : formatPlaceTitle(text);
}

const GENERIC_SEA_NAMES = new Set(['cruising', 'cruising day', 'at sea', 'sea day', 'day at sea', 'a day at sea']);

function seaDayTitle(name) {
  const text = typeof name === 'string' ? name.trim() : '';
  if (!text || GENERIC_SEA_NAMES.has(text.toLowerCase())) return 'Day at sea';
  return displayPortName(text);
}

function payloadStops(payload) {
  return payload && typeof payload === 'object' && Array.isArray(payload.stops) ? payload.stops : [];
}

/**
 * The backend's classified stop for schedule day `dayIndex`. The backend list
 * has only named rows, so align on the count of named days before this one,
 * and verify the port name; fall back to the first non-turnaround match.
 */
function payloadStopFor(payload, days, dayIndex) {
  const stops = payloadStops(payload);
  if (!stops.length) return null;
  const name = nameOf(days[dayIndex]);
  if (!name) return null;
  let named = 0;
  for (let i = 0; i < dayIndex; i += 1) if (nameOf(days[i])) named += 1;
  const positional = stops[named];
  if (positional && portsMatch(positional.port_name, name)) return positional;
  return stops.find(
    (stop) => stop && portsMatch(stop.port_name, name) && stop.kind !== 'embark' && stop.kind !== 'debark',
  ) || null;
}

function realExperiences(payload, portName) {
  const ports = payload && typeof payload === 'object' && Array.isArray(payload.ports) ? payload.ports : [];
  const match = ports.find((port) => port && portsMatch(port.port_name, portName));
  if (!match) return { list: [], attractionUrl: '' };
  const list = (Array.isArray(match.experiences) ? match.experiences : []).filter(
    (experience) => experience
      && typeof experience.title === 'string'
      && experience.title.trim()
      && isRealTourUrl(experience.product_url),
  );
  const url = match.attraction && match.attraction.url;
  return { list, attractionUrl: isRealViatorUrl(url) ? url.trim() : '' };
}

function tourRow(experience) {
  return {
    key: experience.product_code || experience.product_url,
    title: experience.title.trim(),
    url: experience.product_url,
    price: formatFromPrice(experience.from_price, experience.currency),
  };
}

function kindFor(day, stop) {
  const name = nameOf(day);
  if (isNonPortDay(day) || (stop && stop.kind === 'sea_day')) return 'sea';
  if (stop && stop.kind === 'private_island') return 'private-island';
  if (/private island/i.test((stop && stop.note) || '')) return 'private-island';
  if (stop && stop.kind === 'embark') return 'embark';
  if (stop && stop.kind === 'debark') return 'debark';
  return name ? 'port' : 'sea';
}

/**
 * @param {object} args
 * @param {Array}  args.days            scoped itinerary days (buildItinerarySection)
 * @param {object} args.payload         /cruises/:id/excursions payload, or null
 * @param {*}      args.duration        nights, for the header line
 * @param {boolean} [args.unpublished]  shell cruise: no tours, no notes
 */
export function buildTimelineModel({ days, payload, duration, unpublished = false }) {
  const list = Array.isArray(days) ? days : [];
  const usePayload = unpublished ? null : payload;

  const stops = list.map((day, index) => {
    const stop = payloadStopFor(usePayload, list, index);
    const kind = kindFor(day, stop);
    const name = nameOf(day);
    const dayNumber = day && day.day != null && day.day !== '' ? day.day : index + 1;

    let note = '';
    if (kind !== 'sea' && stop && typeof stop.note === 'string' && stop.note.trim()) {
      note = stop.note.trim();
    }

    let tours = [];
    let viatorUrl = '';
    if (kind !== 'sea' && kind !== 'private-island' && name) {
      const found = realExperiences(usePayload, name);
      tours = found.list.slice(0, TOURS_PER_STOP).map(tourRow);
      // The payload is capped (backend MAX_PER_PORT) and carries no real total, so the
      // link names no number. Only with an attraction URL and a rendered tour block.
      viatorUrl = tours.length > 0 ? found.attractionUrl : '';
    }

    return {
      key: `${dayNumber}-${index}`,
      dayNumber,
      dayLabel: `Day ${dayNumber}`,
      dateLabel: day && day.dateYmd ? formatTimelineDate(day.dateYmd) : '',
      kind,
      title: kind === 'sea' ? seaDayTitle(name) : displayPortName(name),
      arrive: (day && day.arrive) || '',
      depart: (day && day.depart) || '',
      description: (day && day.description) || '',
      note,
      tours,
      viatorUrl,
      seeAllLabel: viatorUrl ? SEE_ALL_LABEL : '',
    };
  });

  const counts = summarizeItinerary(list, []);
  return {
    stops,
    summary: {
      nights: duration,
      ports: counts.ports,
      seaDays: counts.seaDays,
      text: list.length ? formatSummaryLine({ nights: duration, ports: counts.ports, seaDays: counts.seaDays }) : '',
    },
    // Render-driven: the Viator disclosure shows once, only when a tour link is on screen.
    hasExcursionSuggestions: stops.some((stop) => stop.tours.length > 0),
  };
}
